import { buildBillHtml } from "./billPrint";
import { withCloudinaryLogoOptimization } from "../../../utils/cloudinary";

// Quotation print module: keeps print-template building and popup/print flow
// outside Dashboard for easier maintenance and debugging.

const formatDateForPrint = (value) => {
  if (!value) return "";
  const date = typeof value?.toDate === "function" ? value.toDate() : new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString("en-GB");
};

const escapeHtml = (value) => String(value ?? "")
  .replace(/&/g, "&amp;")
  .replace(/</g, "&lt;")
  .replace(/>/g, "&gt;")
  .replace(/"/g, "&quot;")
  .replace(/'/g, "&#39;");

const numberToWords = (value) => {
  const num = Math.floor(Number(value) || 0);
  if (!num) return "Zero";
  const ones = ["", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine"];
  const teens = ["Ten", "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen", "Seventeen", "Eighteen", "Nineteen"];
  const tens = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];

  const twoDigits = (n) => {
    if (n < 10) return ones[n];
    if (n < 20) return teens[n - 10];
    const tail = n % 10;
    return `${tens[Math.floor(n / 10)]}${tail ? ` ${ones[tail]}` : ""}`.trim();
  };

  const threeDigits = (n) => {
    if (n < 100) return twoDigits(n);
    const tail = n % 100;
    return `${ones[Math.floor(n / 100)]} Hundred${tail ? ` ${twoDigits(tail)}` : ""}`.trim();
  };

  const parts = [];
  const crore = Math.floor(num / 10000000);
  const lakh = Math.floor((num % 10000000) / 100000);
  const thousand = Math.floor((num % 100000) / 1000);
  const hundred = num % 1000;
  if (crore) parts.push(`${threeDigits(crore)} Crore`);
  if (lakh) parts.push(`${threeDigits(lakh)} Lakh`);
  if (thousand) parts.push(`${threeDigits(thousand)} Thousand`);
  if (hundred) parts.push(threeDigits(hundred));
  return parts.join(" ").trim();
};


export const buildQuotationHtml = ({ quotation, customer, branding, items, totalQuantity, assetBaseUrl, showPackagingCharges = true }) => {
  const quotationDate = formatDateForPrint(quotation.created_at) || formatDateForPrint(new Date());
  const phone = branding.phone;
  const whatsapp = branding.whatsapp;
  const totalInWords = numberToWords(quotation.grand_total || 0);
  const website = branding.website || "www.nilamsynthetics.com";
  const cssUrl = `${assetBaseUrl}/css/quotationPrint.css`;
  const qrCodes = [
    { src: `${assetBaseUrl}/qr-codes/nilam_website_qr.png`, alt: "Website QR", text: "Visit our website" },
    { src: `${assetBaseUrl}/qr-codes/nilam_whatsapp_comm_qr.png`, alt: "WhatsApp QR", text: "Chat with us on WhatsApp" },
    { src: `${assetBaseUrl}/qr-codes/nilam_insta_qr.png`, alt: "Instagram QR", text: "Follow us on Instagram" },
    { src: `${assetBaseUrl}/qr-codes/nilam_google_review_qr.png`, alt: "Google Review QR", text: "Leave a review on Google" },
  ];

  return `
      <!doctype html>
      <html>
        <head>
          <meta charset="utf-8" />
          <title>Quotation ${escapeHtml(quotation.quotation_number || "")}</title>
          <link rel="stylesheet" href="${cssUrl}" />
        </head>
        <body>
          <div class="sheet">
            <div class="title">Quotation</div>
            <div class="head-row">
              <div class="brand-wrap">
                ${branding.logoUrl ? `<img src="${escapeHtml(withCloudinaryLogoOptimization(branding.logoUrl))}" class="logo" alt="Logo" />` : ""}
                <div class="brand-text">
                  <div class="strong" style="font-size:20px">${escapeHtml(branding.brandName || "Nilam Synthetics")}</div>
                  <div class="subtle">${escapeHtml(branding.address || "")}</div>
                </div>
              </div>
              <div style="text-align:right; display:flex; flex-direction:column; align-items:flex-end; gap:4px;">  
                ${phone ? `
                  <div class="meta-item" style="display:flex; align-items:center; gap:6px;">
                    <span>📞</span>
                    <span>${escapeHtml(phone)}</span>
                  </div>
                ` : ""}

                ${whatsapp ? `
                  <div class="meta-item" style="display:flex; align-items:center; gap:6px;">
                    <span style="display:flex; align-items:center;">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="#25D366">
                        <path d="M12 2a10 10 0 00-8.7 15l-1.3 5 5.1-1.3A10 10 0 1012 2zm0 18a8 8 0 01-4.1-1.1l-.3-.2-3 .8.8-2.9-.2-.3A8 8 0 1112 20zm4.3-5.7c-.2-.1-1.3-.6-1.5-.7s-.4-.1-.6.1-.7.7-.8.8-.3.1-.5 0a6.6 6.6 0 01-2-1.2 7.3 7.3 0 01-1.4-1.8c-.1-.2 0-.3.1-.4l.3-.3c.1-.1.2-.2.3-.4.1-.2 0-.3 0-.4s-.6-1.4-.8-1.9c-.2-.5-.4-.4-.6-.4h-.5c-.2 0-.4.1-.6.3s-.8.8-.8 2 .8 2.4.9 2.6a9.8 9.8 0 003.8 3.6c.5.2.9.4 1.2.5.5.2 1 .2 1.4.1.4-.1 1.3-.5 1.5-1 .2-.5.2-.9.1-1-.1-.1-.3-.2-.5-.3z"/>
                      </svg>
                    </span>
                    <span>${escapeHtml(whatsapp)}</span>
                  </div>
                ` : ""}
                <div class="meta-item">${escapeHtml(website)}</div>

                ${!phone && !whatsapp ? `<div class="meta-item">—</div>` : ""}
              </div>
            </div>

            <div class="meta-wrap">
              <div class="meta-col">
                <p class="meta-item"><span class="strong">Customer:</span> ${escapeHtml(quotation.customer_name || "—")}</p>
                <p class="meta-item"><span class="strong">Address:</span> ${escapeHtml(quotation.customer_address || customer.address || "—")} ${escapeHtml(quotation.customer_city || "—")} - ${escapeHtml(quotation.customer_pincode || customer.pincode || "—")}</p>
                <p class="meta-item"><span class="strong">Mobile:</span> ${escapeHtml(quotation.customer_mobile || "—")}</p>
              </div>
              <div class="meta-col">
                <p class="meta-item"><span class="strong">Quotation No:</span> ${escapeHtml(quotation.quotation_number || "—")}</p>
                <p class="meta-item"><span class="strong">Date:</span> ${escapeHtml(quotationDate || "—")}</p>
                <p class="meta-item"><span class="strong">Transport:</span> ${escapeHtml(quotation.transport_name || customer.transport_name || "—")}</p>
                <p class="meta-item"><span class="strong">Additional Details:</span> —</p>
              </div>
            </div>

            <table class="${items.length > 9 ? 'compact' : ''}">
              <thead>
                <tr>
                  <th style="width:56px">Sr.</th>
                  <th>Details</th>
                  <th style="width:92px">Pcs.</th>
                  <th style="width:110px">Rate</th>
                  <th style="width:120px">Total</th>
                </tr>
              </thead>
              <tbody>
                ${items.map(item => `
                  <tr>
                    <td>${escapeHtml(item.serial)}</td>
                    <td>D. No: ${escapeHtml(item.detail)}</td>
                    <td>${escapeHtml(item.quantity)}</td>
                    <td class="right">${escapeHtml(Number(item.price).toFixed(2))}</td>
                    <td class="right">${escapeHtml(Number(item.total).toFixed(2))}</td>
                  </tr>
                `).join("")}
                ${Array.from({ length: Math.max(0, 12 - items.length) }).map(() => `
                  <tr><td>&nbsp;</td><td></td><td></td><td></td><td></td></tr>
                `).join("")}
                <tr class="total-row">
                  <td colspan="2" class="right">Total Quantity</td>
                  <td class="right">${escapeHtml(totalQuantity)}</td>
                  <td class="right">Sub Total</td>
                  <td class="right">${escapeHtml(Number(quotation.subtotal_amount || 0).toFixed(2))}</td>
                </tr>
                ${Number(quotation.discount_amount || 0) > 0 ? `
                <tr class="total-row">
                  <td colspan="4" class="right">Discount${Number(quotation.discount_percentage) > 0 ? ` (${escapeHtml(String(Number(quotation.discount_percentage)))}%)` : ""}</td>
                  <td class="right">-${escapeHtml(Number(quotation.discount_amount || 0).toFixed(2))}</td>
                </tr>` : ""}
                ${showPackagingCharges && Number(quotation.packaging_charges || 0) > 0 ? `
                <tr class="total-row">
                  <td colspan="4" class="right">Packaging Charges</td>
                  <td class="right">${escapeHtml(Number(quotation.packaging_charges || 0).toFixed(2))}</td>
                </tr>` : ""}
                ${Number(quotation.transport_charges || 0) > 0 ? `
                <tr class="total-row">
                  <td colspan="4" class="right">Transportation Charges</td>
                  <td class="right">${escapeHtml(Number(quotation.transport_charges || 0).toFixed(2))}</td>
                </tr>` : ""}
                ${Number(quotation.round_off_amount || 0) !== 0 ? `
                <tr class="total-row">
                  <td colspan="4" class="right">Round Off</td>
                  <td class="right">${Number(quotation.round_off_amount || 0) > 0 ? "+" : ""}${escapeHtml(Number(quotation.round_off_amount || 0).toFixed(2))}</td>
                </tr>` : ""}
                <tr class="total-row">
                  <td colspan="3">Amount in words:</span> ${escapeHtml(totalInWords)} Only</td>
                  <td class="right">Grand Total</td>
                  <td class="right">${escapeHtml(Number(quotation.grand_total || 0).toFixed(2))}</td>
                </tr>

              </tbody>
            </table>

            <div class="words"><span class="strong">1. Goods once sold will not be taken back. <br/>
              2. No guarantee for color bleeding and fabric shrinkage.</span></div>

            <div class="footer" display="${items.length > 12 ? 'none' : 'block'}">
              <div class="qr-wrap">
                ${qrCodes.map((qrCode) => `
                  <div class="qr-item">
                    <div class="qr">
                      <img src="${escapeHtml(qrCode.src)}" alt="${escapeHtml(qrCode.alt)}" />
                    </div>
                    <div class="qr-text">
                      ${escapeHtml(qrCode.text || "")}
                    </div>
                  </div>
                `).join("")}
              </div>
            </div>
          </div>
        </body>
      </html>
    `;
};

export const printQuotationDocument = ({ quotation, customers, designs, branding, showPackagingCharges = true, onError }) => {
  const customer = customers.find(c => c.id === quotation.customer_id) || {};
  const items = (quotation.items || []).map((item, index) => {
    const design = designs.find(d => d.id === item.design_id);
    const detail = item.design_no || design?.designNo || "—";
    const quantity = Math.max(1, Number(item.quantity) || 1);
    const price = Number(item.price || 0);
    const total = Number(item.subtotal || (price * quantity));
    return {
      serial: item.serial || index + 1,
      detail,
      quantity,
      price,
      total,
    };
  });
  const totalQuantity = items.reduce((sum, item) => sum + (Number(item.quantity) || 0), 0);

  const assetBaseUrl = window.location.origin;
  const hasBillNumber = Boolean(String(quotation.bill_number || "").trim());
  const html = hasBillNumber
    ? buildBillHtml({ quotation, customer, branding, items, assetBaseUrl, showPackagingCharges })
    : buildQuotationHtml({ quotation, customer, branding, items, totalQuantity, assetBaseUrl, showPackagingCharges });
  const printWindow = window.open("", "_blank", "width=900,height=1100");
  if (!printWindow) {
    onError?.("Please allow popups to print quotation");
    return;
  }

  try {
    printWindow.document.open();
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.focus();

    const closePrintWindow = () => {
      if (!printWindow.closed) {
        printWindow.close();
      }
    };

    printWindow.onafterprint = closePrintWindow;

    window.setTimeout(() => {
      printWindow.print();

      // Fallback for browsers where `onafterprint` is unreliable.
      window.setTimeout(closePrintWindow, 1500);
    }, 250);
  } catch (error) {
    printWindow.close();
    onError?.("Unable to open printable quotation. Please try again.");
  }
};
